import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { submitBulkScanLabels } from "actions/modals/scanLabel";

import { BulkUpdateOptionalFieldsModal } from "components/BulkUpdateOptionalFieldsModal/Modal";
import { ModelNames } from "components/BulkUpdateOptionalFieldsModal/util";

import { ModalTypes } from "constants/navigation";
import { ScanLabelArchetype } from "constants/scanLabels";

import { getLivestockSaleId } from "lib/navigation";

import { getContextByModalType, getScanLabels } from "selectors";

import { ScanLabelFields } from "./Fields";

interface BulkUpdateScanLabelsModalContext {
  scanIds?: string[];
}

interface Props {
  onClose: () => void;
}

interface ScanLabelPayload {
  id: string;
  addLabels?: string[];
  removeLabels?: string[];
}

export const BulkUpdateScanLabelsModal: React.FC<Props> = ({ onClose }) => {
  const context = useSelector<RootState, BulkUpdateScanLabelsModalContext>(
    getContextByModalType(ModalTypes.BulkUpdateScanLabels),
  );
  const scanIds = useMemo(() => context?.scanIds || [], [context?.scanIds]);

  const scanLabelsById = useSelector(getScanLabels);
  const livestockSaleId = getLivestockSaleId();

  const archetypeByLabelName = useMemo(() => {
    const archetypeByName: Record<string, ScanLabelArchetype> = {};

    Object.values(ScanLabelArchetype)
      .filter(archetype => archetype !== ScanLabelArchetype.NONE)
      .forEach(archetype => {
        archetypeByName[archetype] = archetype;
      });

    Object.values(scanLabelsById as Record<string, ScanLabel>)
      .filter(scanLabel => scanLabel.livestockSaleId === livestockSaleId)
      .forEach(scanLabel => {
        if (!(scanLabel.name in archetypeByName)) {
          archetypeByName[scanLabel.name] = scanLabel.archetype;
        }
      });

    return archetypeByName;
  }, [scanLabelsById, livestockSaleId]);

  const bulkUpdateActionCreator = useCallback(
    (payloads: ScanLabelPayload[]) => {
      const ids = payloads.map(payload => payload.id);
      const { addLabels = [], removeLabels = [] } = payloads[0] || {};
      return submitBulkScanLabels(
        ids,
        addLabels,
        removeLabels,
        archetypeByLabelName,
      );
    },
    [archetypeByLabelName],
  );

  return (
    <BulkUpdateOptionalFieldsModal
      onClose={onClose}
      modelIds={scanIds}
      modelName={ModelNames.SCAN}
      bulkUpdateActionCreator={bulkUpdateActionCreator}
      fields={ScanLabelFields}
      initialValues={{}}
    />
  );
};
